// Two-step logging like iOS: 1) tap the muscles on the body, 2) rate intensity and recovery.
import { useRef, useState } from 'react';
import type { Session } from '../backend/types.ts';
import { activityLabel, SOLO_ACTIVITIES, suggestedMuscles } from '../lib/activities.ts';
import { groups, musclesIn } from '../lib/catalog.ts';
import { atLocal, localDay, localTime, minutesBetween, recentDays } from '../lib/format.ts';
import {
  fromMuscles, isEmpty, isSelected, selectedMuscles, setGroup, toggleSpecific, type Selection,
} from '../lib/selection.ts';
import { useModel } from '../state.tsx';
import { BodyMap, type BodyMapHandle } from '../ui/BodyMap.tsx';
import { Card, RATING_LABELS, RatingPicker, Segmented, Sheet, Switch } from '../ui/components.tsx';

export function LogSession({ session: initial, onClose }: { session: Session; onClose: () => void }) {
  const model = useModel();
  const [session, setSession] = useState(initial);
  const [selection, setSelection] = useState<Selection>(() => fromMuscles(initial.muscles));
  const [page, setPage] = useState<0 | 1>(0);
  const [list, setList] = useState(false);
  const [saving, setSaving] = useState(false);
  const [zoomed, setZoomed] = useState<string | null>(null);
  const map = useRef<BodyMapHandle>(null);
  const isNew = initial.status !== 'logged';
  const title = session.kind === 'trainer' ? 'Session with trainer' : activityLabel(session.activity_type);
  const inFuture = new Date(session.started_at).getTime() > Date.now();
  const canSave = session.intensity != null && session.recovery != null && !isEmpty(selection) && !inFuture;

  const setKind = (kind: Session['kind']) => setSession((s) => ({ ...s, kind, activity_type: kind === 'trainer' ? 'strength' : 'running' }));
  const setActivity = (activity_type: string) => {
    setSession((s) => ({ ...s, activity_type }));
    if (isEmpty(selection)) setSelection(fromMuscles(suggestedMuscles(activity_type)));
  };

  const save = async () => {
    setSaving(true);
    const ok = await model.log({ ...session, muscles: selectedMuscles(selection), note: session.note?.trim() || null });
    setSaving(false);
    if (ok) onClose();
  };

  if (list) {
    return (
      <Sheet title="Muscles" onClose={() => setList(false)} leading={<span style={{ minWidth: 56 }} />}>
        <MuscleList selection={selection} onChange={setSelection} />
      </Sheet>
    );
  }

  return (
    <Sheet title={title} onClose={onClose}
      leading={page === 1 ? <button className="link" onClick={() => setPage(0)}>‹ Back</button> : undefined}
      footer={page === 0 && zoomed ? (
        <button key="done" className="btn primary" onClick={() => map.current?.done()} data-testid="bm-done">Done</button>
      ) : page === 0 ? (
        <button key="next" className="btn primary" disabled={isEmpty(selection)} onClick={() => setPage(1)} data-testid="log-next">Next</button>
      ) : (
        <button className="btn primary" disabled={!canSave || saving} onClick={save} data-testid="log-save">{saving ? 'Saving…' : 'Save'}</button>
      )}>
      {page === 0 ? (
        <>
          <h1 className="title-md">What did you work?</h1>
          {session.kind === 'solo' && isNew && (
            <select className="field" value={session.activity_type} onChange={(e) => setActivity(e.target.value)} aria-label="Activity">
              {SOLO_ACTIVITIES.map((a) => <option key={a} value={a}>{activityLabel(a)}</option>)}
            </select>
          )}
          <BodyMap ref={map} externalDone onFocusChange={setZoomed} avatar={model.profile?.avatar ?? 'male'}
            mode={{ kind: 'select', selection, onChange: setSelection }} />
          <div className="row">
            <button className="link" onClick={() => setList(true)} data-testid="open-muscle-list">Use the list instead</button>
            <span className="spacer" />
            {!isEmpty(selection) && <button className="link" onClick={() => setSelection(fromMuscles([]))}>Clear all</button>}
          </div>
        </>
      ) : (
        <>
          <Card>
            <Segmented label="Session type" options={[['trainer', 'With trainer'], ['solo', 'On my own']]} value={session.kind} onChange={setKind} />
            {session.kind === 'solo' && (
              <select className="field" value={session.activity_type} onChange={(e) => setActivity(e.target.value)} aria-label="Activity">
                {SOLO_ACTIVITIES.map((a) => <option key={a} value={a}>{activityLabel(a)}</option>)}
              </select>
            )}
            <WhenAndHowLong session={session} onChange={setSession} />
          </Card>
          <Card>
            <RatingPicker title="Intensity" labels={RATING_LABELS.intensity} value={session.intensity} invert
              onChange={(intensity) => setSession((s) => ({ ...s, intensity }))} />
          </Card>
          <Card>
            <RatingPicker title="Recovery" labels={RATING_LABELS.recovery} value={session.recovery}
              onChange={(recovery) => setSession((s) => ({ ...s, recovery }))} />
            <p className="caption">How recovered did you feel going into this session?</p>
          </Card>
          <Card>
            <span className="eyebrow">Note (optional)</span>
            <textarea className="field" maxLength={2000} placeholder="Anything your trainer should know?" value={session.note ?? ''}
              onChange={(e) => setSession((s) => ({ ...s, note: e.target.value }))} />
          </Card>
          {!isNew && (
            <button className="btn danger" onClick={async () => { await model.remove(session); onClose(); }}>Delete this session</button>
          )}
        </>
      )}
    </Sheet>
  );
}

const DURATIONS = [30, 45, 60, 75, 90, 120];

/** Day, start time and length of the session (stored as started_at / ended_at). */
function WhenAndHowLong({ session, onChange }: { session: Session; onChange: (fn: (s: Session) => Session) => void }) {
  const minutes = minutesBetween(session.started_at, session.ended_at) ?? 60;
  const day = localDay(session.started_at);
  const time = localTime(session.started_at);
  const days = recentDays();
  const setStart = (d: string, t: string) => onChange((s) => {
    const started_at = atLocal(d, t);
    return { ...s, started_at, ended_at: new Date(new Date(started_at).getTime() + minutes * 60_000).toISOString() };
  });
  const setMinutes = (m: number) => onChange((s) => ({
    ...s, ended_at: new Date(new Date(s.started_at).getTime() + m * 60_000).toISOString(),
  }));
  const future = new Date(session.started_at).getTime() > Date.now();
  return (
    <>
      <div className="row" style={{ gap: 8 }}>
        <label className="stack" style={{ gap: 6, flex: 3 }}>
          <span className="caption">Day</span>
          <select className="field" value={day} onChange={(e) => setStart(e.target.value, time)} data-testid="log-day">
            {!days.some((d) => d.value === day) && <option value={day}>{day}</option>}
            {days.map((d) => <option key={d.value} value={d.value}>{d.label}</option>)}
          </select>
        </label>
        <label className="stack" style={{ gap: 6, flex: 2 }}>
          <span className="caption">Start time</span>
          <input className="field" type="time" step={300} value={time} data-testid="log-time"
            onChange={(e) => e.target.value && setStart(day, e.target.value)} />
        </label>
      </div>
      {future && <p className="caption danger" role="alert">That time is in the future.</p>}
      <div className="stack" style={{ gap: 6 }} role="group" aria-label="How long">
        <span className="caption">How long</span>
        <div className="chips">
          {DURATIONS.map((m) => (
            <button key={m} type="button" aria-pressed={minutes === m} onClick={() => setMinutes(m)} data-testid={`duration-${m}`}>
              {m < 120 ? `${m} min` : '2 h'}
            </button>
          ))}
          <label className="chip-input">
            <input type="number" inputMode="numeric" min={5} max={300} step={5} aria-label="Minutes"
              value={DURATIONS.includes(minutes) ? '' : minutes} placeholder="Other"
              onChange={(e) => { const m = Number(e.target.value); if (m >= 5 && m <= 300) setMinutes(m); }} />
          </label>
        </div>
      </div>
    </>
  );
}

/** Accessible alternative to the body: every group and muscle as switches. */
function MuscleList({ selection, onChange }: { selection: Selection; onChange: (s: Selection) => void }) {
  return (
    <>
      {groups.map((g) => (
        <Card key={g.id}>
          <div className="toggle-row">
            <div className="text"><h2>{g.name}</h2><p className="caption">Whole group</p></div>
            <Switch label={`${g.name}, whole group`} checked={selection.groups.has(g.id)} onChange={(v) => onChange(setGroup(selection, g.id, v))} />
          </div>
          {musclesIn(g.id).length > 1 && musclesIn(g.id).map((m) => (
            <div className="toggle-row" key={m.id}>
              <div className="text"><span>{m.name}</span></div>
              <Switch label={m.name} checked={isSelected(selection, m.id)} onChange={() => onChange(toggleSpecific(selection, m.id))} />
            </div>
          ))}
        </Card>
      ))}
    </>
  );
}
